import IconAccountFill from '@rendora/design/assets/account-fill.svg?component';
import IconAccount from '@rendora/design/assets/account.svg?component';
import IconConsumeFill from '@rendora/design/assets/consume-fill.svg?component';
import IconConsume from '@rendora/design/assets/consume.svg?component';
import IconEquityFill from '@rendora/design/assets/equity-fill.svg?component';
import IconEquity from '@rendora/design/assets/equity.svg?component';
import IconOrderFill from '@rendora/design/assets/order-fill.svg?component';
import IconOrder from '@rendora/design/assets/order.svg?component';
import IconOrganizationFill from '@rendora/design/assets/organization-fill.svg?component';
import IconOrganization from '@rendora/design/assets/organization.svg?component';
import { USER_LEVEL_KEY } from '~/api/interface';
import { USER_ADMIN } from '~/constants';
import { PageKey } from '~/constants/page';
import { useDetector } from '~/hooks/useDetector';

export interface Nav {
  name: string;
  route?: string;
  pageKey?: PageKey;
  link?: string;
  children?: Nav[];
  icon?: string;
  iconActive?: string;
  iconGlobal?: string;
  crown?: boolean;
  dropIcon?: string;
  hidden?: boolean;
  trackEvent?: TRACK_EVENT;
  target?: string;
  description?: string;
}

export const useTopNav = () => {
  const { $t } = useNuxtApp();
  const { hasPagePermission } = usePageAndActionPermissionData();

  return computed<Nav[]>(() => {
    const base = [
      // {
      //   name: $t('shou-ye'),
      //   trackEvent: TRACK_EVENT.CLICK_HOME_TOP,
      //   pageKey: PageKey.PRIME,
      // },
      {
        name: $t('3d-shu-zi-ren'),
        pageKey: PageKey.AI_AVATAR,
        route: '/3d-ai-avatar',
        children: [
          {
            route: '/3d-avatar',
            pageKey: PageKey.CHARACTER_DETAIL,
          },
          {
            route: '/characters',
            pageKey: PageKey.CHARACTER,
          },
        ],
        // pageKey: PageKey.AI_AVATAR,
        // TODO 更换页面埋点
        // trackEvent: TRACK_EVENT.CLICK_CHARACTER,
      },
      {
        name: $t('ai-shi-pin-sheng-cheng'),
        route: '/ai-avatar-video-generation',
        pageKey: PageKey.AI_AVATAR_VIDEO_GENERATION,
        // FIXME： 替换页面埋点
        // trackEvent: TRACK_EVENT.CLICK_TEMPLATE_CENTER,
      },
      {
        name: $t('ying-yong-chang-jing'),
        pageKey: PageKey.SCENARIOS,
      },
      {
        name: $t('fu-fei-ding-yue'),
        route: '/subscribe',
        pageKey: PageKey.SUBSCRIBE,
        trackEvent: TRACK_EVENT.CLICK_SUBSCRIBE,
      },
      {
        name: $t('mo-fa-xue-yuan'),
        route: '/academy',
        pageKey: PageKey.ACADEMY,
        trackEvent: TRACK_EVENT.CLICK_SCHOOL,
        children: [
          {
            route: '/blog',
            pageKey: PageKey.BLOG,
          },
          {
            route: '/case',
            pageKey: PageKey.CASE_DETAIL,
          },
        ],
      },
      {
        name: $t('bang-zhu-zhong-xin'),
        route: '/help-center',
        pageKey: PageKey.HELP_CENTER,
        icon: 'icon-help-center',
        trackEvent: TRACK_EVENT.CLICK_HELP_CENTER,
        description: $t('zai-zhe-li-kuai-su-cha-zhao-bang-zhu-wen-dang'),
      },
    ] as Nav[];

    return base.filter(item => {
      if (!item) {
        return false;
      }

      if (item.pageKey === PageKey.PRIME) {
        return true;
      }

      if (item.children && item.children.length <= 0) {
        return false;
      }

      // 假如 pageKey 不存在，或者有权限，则返回 true
      return !item.pageKey || hasPagePermission(item.pageKey);
    });
  });
};

export const useHomeNav = () => {
  const { $t: t } = useNuxtApp();
  const { hasPagePermission } = usePageAndActionPermissionData();

  return computed<Nav[]>(() =>
    [
      {
        name: t('shou-ye'),
        route: '/platform',
        pageKey: PageKey.HOME,
        icon: 'home-platform',
        iconGlobal: 'home-global',
        trackEvent: TRACK_EVENT.CLICK_HOME_LEFT,
      },
      {
        name: t('zui-jin-zuo-pin'),
        route: '/home/recently',
        pageKey: PageKey.RECENTLY_WORKS,
        icon: 'home-recently',
        iconGlobal: 'recent-opus-global',
        trackEvent: TRACK_EVENT.CLICK_RECENTLY_WORK,
      },
      {
        name: t('wo-de-shou-cang'),
        route: '/home/collection',
        pageKey: PageKey.MY_COLLECTION,
        icon: 'home-collection',
        iconGlobal: 'collect-global',
        trackEvent: TRACK_EVENT.CLICK_COLLECT,
      },
      {
        name: t('ge-ren-kong-jian'),
        route: '/home/personal',
        pageKey: PageKey.PERSONAL_SPACE,
        icon: 'home-personal',
        iconGlobal: 'personal-space-global',
        trackEvent: TRACK_EVENT.CLICK_PERSONAL_SPACE,
      },
      {
        name: t('gong-xiang-kong-jian'),
        route: '/home/team',
        pageKey: PageKey.TEAM_SPACE,
        icon: 'home-shared',
        iconGlobal: 'share-space-global',
        crown: true,
        trackEvent: TRACK_EVENT.CLICK_TEAM_SPACE,
      },
      {
        name: t('pin-pai-gui-fan'),
        route: '/home/brand',
        pageKey: PageKey.BRAND_MANAGE,
        icon: 'home-brand',
        iconGlobal: 'brand-kit-global',
        crown: true,
        trackEvent: TRACK_EVENT.CLICK_BRAND_KIT,
      },
      {
        name: 'lab实验室',
        route: '/home/lab',
        pageKey: PageKey.LAB,
        icon: 'lab',
        iconGlobal: 'lab-fill',
        trackEvent: TRACK_EVENT.CLICK_BRAND_KIT,
      },
    ].filter(item => hasPagePermission(item.pageKey))
  );
};

export const useCenterNav = () => {
  const { isTeamRight } = useDetector();
  const userInfo = useUserInfo();
  const { $t } = useNuxtApp();
  const { hasPagePermission, hasActionPermission } = usePageAndActionPermissionData();

  return computed<Nav[]>(() =>
    [
      {
        name: $t('zhang-hao-she-zhi'),
        route: '/center/account',
        pageKey: PageKey.CENTER_ACCOUNT,
        icon: 'user-outline',
        iconGlobal: 'user-outline',
        workspaceIcon: {
          icon: IconAccount,
          iconFill: IconAccountFill,
        },
      },
      {
        name: $t('tuan-dui-guan-li'),
        route: '/center/member',
        pageKey: PageKey.CENTER_MEMBER,
        icon: 'user-group-outline',
        iconGlobal: 'user-group-outline',
        rights_type: [
          USER_LEVEL_KEY.ORG_FLAGSHIP,
          USER_LEVEL_KEY.ORG_STANDARD,
          USER_LEVEL_KEY.TEAM_LIGHT,
          USER_LEVEL_KEY.TEAM_TASTE,
        ],
        permission: [
          USER_ADMIN.ORG_ADMIN_USER,
          USER_ADMIN.SUPER_ADMIN,
          USER_ADMIN.MANAGEMENT_PLATFORM,
        ],
        workspaceIcon: {
          icon: IconOrganization,
          iconFill: IconOrganizationFill,
        },
      },
      {
        name: $t('xiao-hao-ji-lu'),
        route: '/center/consume',
        pageKey: PageKey.CENTER_CONSUME,
        icon: 'user-cost',
        iconGlobal: 'user-cost',
        rights_type: [
          USER_LEVEL_KEY.PERSONAL,
          USER_LEVEL_KEY.TASTE,
          USER_LEVEL_KEY.ORG_FLAGSHIP,
          USER_LEVEL_KEY.ORG_STANDARD,
          USER_LEVEL_KEY.TEAM_LIGHT,
          USER_LEVEL_KEY.TEAM_TASTE,
          USER_LEVEL_KEY.PERSONAL_LITE,
        ],
        permission: [USER_ADMIN.NORMAL],
        workspaceIcon: {
          icon: IconConsume,
          iconFill: IconConsumeFill,
        },
      },
      {
        name: $t('quan-yi-guan-li'),
        route: '/center/equity',
        pageKey: PageKey.CENTER_EQUITY,
        icon: 'user-benefit',
        iconGlobal: 'user-benefit',
        rights_type: [
          USER_LEVEL_KEY.PERSONAL,
          USER_LEVEL_KEY.TASTE,
          USER_LEVEL_KEY.ORG_FLAGSHIP,
          USER_LEVEL_KEY.ORG_STANDARD,
          USER_LEVEL_KEY.TEAM_LIGHT,
          USER_LEVEL_KEY.TEAM_TASTE,
          USER_LEVEL_KEY.PERSONAL_LITE,
        ],
        permission: [
          USER_ADMIN.ORG_ADMIN_USER,
          USER_ADMIN.SUPER_ADMIN,
          USER_ADMIN.MANAGEMENT_PLATFORM,
        ],
        workspaceIcon: {
          icon: IconEquity,
          iconFill: IconEquityFill,
        },
      },
      {
        name: $t('ding-dan-fa-piao'),
        route: '/center/order',
        pageKey: PageKey.CENTER_ORDER,
        icon: 'user-order',
        iconGlobal: 'user-order',
        rights_type: [
          USER_LEVEL_KEY.PERSONAL,
          USER_LEVEL_KEY.TASTE,
          USER_LEVEL_KEY.ORG_FLAGSHIP,
          USER_LEVEL_KEY.ORG_STANDARD,
          USER_LEVEL_KEY.TEAM_LIGHT,
          USER_LEVEL_KEY.TEAM_TASTE,
          USER_LEVEL_KEY.PERSONAL_LITE,
        ],
        permission: [
          USER_ADMIN.ORG_ADMIN_USER,
          USER_ADMIN.SUPER_ADMIN,
          USER_ADMIN.MANAGEMENT_PLATFORM,
        ],
        workspaceIcon: {
          icon: IconOrder,
          iconFill: IconOrderFill,
        },
      },
      {
        name: isTeamRight.value ? $t('tuan-dui-zhu-ye') : $t('ge-ren-zhu-ye'),
        route: `/personal-page/${userInfo.value?.organization_id}.html`,
        pageKey: PageKey.PERSONAL_PAGE,
        icon: 'homepage',
        iconGlobal: 'homepage',
      },
    ].filter(item => {
      if (!hasPagePermission(item.pageKey)) {
        return false;
      }

      if (item.pageKey === PageKey.PERSONAL_PAGE) {
        if (
          isTeamRight.value &&
          userInfo.value?.permission === USER_ADMIN.NORMAL &&
          !hasActionPermission('personal_page_member_view')
        ) {
          return false;
        }
      }

      return true;
    })
  );
};
